/**
 * Favorites store for managing user favorites
 * 
 * This store handles caching and syncing of favorited items (library items, documents, blueprints)
 */

import { storeRegistry } from "@/rerun-manager/GenericStore";
import { apiService } from "@/lib/api-functions/api-service";

export interface FavoriteItem {
  id: string;
  user_id: string;
  item_id: string;
  item_type: 'library' | 'document' | 'blueprint';
  game_id: string;
  created_at: string;
}

/**
 * Store for favorites, using GenericStore
 */
export const favoritesStore = storeRegistry.getStore<FavoriteItem>({
  key: 'favorites',
  ttlMs: 1000 * 60 * 30, // 30 minutes cache
});

/**
 * Get cached favorites for a specific user and game
 * @param userId - The user ID to get favorites for
 * @param gameId - The game ID to filter by
 */
export function getCachedFavorites(userId: string, gameId: string): FavoriteItem[] {
  const allFavorites = favoritesStore.getData();
  return allFavorites.filter(fav => 
    fav.user_id === userId && fav.game_id === gameId
  );
}

/**
 * Get cached favorite items for a specific item type
 * @param userId - The user ID
 * @param gameId - The game ID
 * @param itemType - The type of items to filter ('library', 'document', 'blueprint')
 */
export function getCachedFavoritesByType(
  userId: string, 
  gameId: string, 
  itemType: 'library' | 'document' | 'blueprint'
): FavoriteItem[] {
  const allFavorites = getCachedFavorites(userId, gameId);
  return allFavorites.filter(fav => fav.item_type === itemType);
}

/**
 * Check if an item is favorited
 * @param userId - The user ID
 * @param itemId - The item ID to check
 */
export function isItemFavorited(userId: string, itemId: string): boolean {
  const allFavorites = favoritesStore.getData();
  return allFavorites.some(fav => fav.user_id === userId && fav.item_id === itemId);
}

/**
 * Fetch favorites for a specific user and game from the API
 * @param userId - The user ID
 * @param gameId - The game ID
 * @param toast - Optional toast function for error reporting
 */
export async function fetchFavorites(
  userId: string,
  gameId: string,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<FavoriteItem[]> {
  try {
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    const result = await apiService.fetchFavorites(userId, gameId);
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to fetch favorites');
    }
    
    const favorites = result.data;
    await favoritesStore.syncFromRemote(async () => favorites);
    return favorites;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to fetch favorites';
    console.error(`[fetchFavorites] Error:`, err);
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Add an item to favorites
 * @param userId - The user ID
 * @param gameId - The game ID
 * @param itemId - The item ID to favorite
 * @param itemType - The type of item ('library', 'document', 'blueprint')
 * @param toast - Optional toast function
 */
export async function addFavorite(
  userId: string,
  gameId: string,
  itemId: string,
  itemType: 'library' | 'document' | 'blueprint',
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<FavoriteItem> {
  try {
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    const result = await apiService.addFavorite({ 
      user_id: userId, 
      game_id: gameId, 
      item_id: itemId, 
      item_type: itemType 
    });
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to add favorite');
    }

    const newFavorite = result.data;
    
    // Add to store
    const currentData = favoritesStore.getData();
    favoritesStore.setData([...currentData, newFavorite]);

    if (toast) {
      toast({
        title: 'Success',
        description: 'Added to favorites',
      });
    }

    return newFavorite;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to add favorite';
    console.error(`[addFavorite] Error:`, err);
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Remove an item from favorites
 * @param userId - The user ID
 * @param itemId - The item ID to unfavorite
 * @param toast - Optional toast function
 */
export async function removeFavorite(
  userId: string,
  itemId: string,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<void> {
  try {
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    const result = await apiService.removeFavorite(userId, itemId);
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to remove favorite');
    }
    
    // Remove from store
    const currentData = favoritesStore.getData();
    const updatedData = currentData.filter(fav => 
      !(fav.user_id === userId && fav.item_id === itemId)
    );
    favoritesStore.setData(updatedData);

    if (toast) {
      toast({
        title: 'Success',
        description: 'Removed from favorites',
      });
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to remove favorite';
    console.error(`[removeFavorite] Error:`, err);
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Clear the favorites cache
 */
export async function clearFavoritesCache(): Promise<void> {
  await favoritesStore.clearCache();
}

